import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, X, ChevronDown } from 'lucide-react';

const DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'lastmonth', label: 'Last Month' },
  { value: 'year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'student', label: 'Student' },
  { value: 'admin', label: 'Admin' },
  { value: 'college_admin', label: 'College Admin' },
  { value: 'recruiter', label: 'Recruiter' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];

// ─── Mini select ──────────────────────────────────────────────────────────────
const Select = ({ label, value, options, onChange, isDark, disabled = false }) => (
  <div className="flex flex-col gap-1 min-w-[140px] flex-1">
    <label className={`text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
      {label}
    </label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none focus:ring-1 focus:ring-[#284BB3] transition-all disabled:opacity-50 ${
        isDark
          ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200 focus:border-[#284BB3]'
          : 'bg-white border-gray-300 text-gray-700 focus:border-[#284BB3]'
      }`}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

// ─── Main FilterBar ────────────────────────────────────────────────────────────
const AnalyticsFilterBar = ({
  filters,
  onChange,
  onReset,
  colleges = [],
  departments = [],
  batches = [],
  isDark,
  user,
}) => {
  const [open, setOpen] = useState(false);

  const update = (key, value) => onChange({ ...filters, [key]: value });

  const activeCount = [
    filters.dateRange && filters.dateRange !== 'year',
    filters.college,
    filters.department,
    filters.batch,
    filters.course,
    filters.role,
    filters.status,
    filters.dateFrom && filters.dateTo,
  ].filter(Boolean).length;

  const collegeOptions = [
    { value: '', label: 'All Colleges' },
    ...colleges.map(c => ({ value: c._id, label: c.collegeName || c.institutionName || c._id })),
  ];

  const deptOptions = [
    { value: '', label: 'All Departments' },
    ...departments.map(d => ({ value: d, label: d })),
  ];

  const batchOptions = [
    { value: '', label: 'All Batches' },
    ...batches.map(b => ({ value: b, label: b })),
  ];

  return (
    <div className={`rounded-2xl border mb-4 overflow-hidden transition-all ${isDark ? 'bg-[#002147] border-[#1a3884]/40 shadow-lg' : 'bg-white border-gray-200 shadow-sm'}`}>
      {/* Toggle row */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-5 py-3.5 text-left transition-colors ${isDark ? 'hover:bg-[#0F225C]/30' : 'hover:bg-gray-50'}`}
      >
        <div className="flex items-center gap-2.5">
          <Filter size={15} className={isDark ? 'text-[#60A5FA]' : 'text-[#284BB3]'} />
          <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Filters</span>
          {activeCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black text-white bg-[#284BB3]">
              {activeCount}
            </span>
          )}
          {activeCount > 0 && (
            <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>active</span>
          )}
        </div>
        <ChevronDown
          size={15}
          className={`transition-transform duration-300 ${open ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
        />
      </button>

      {/* Filter rows */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className={`px-5 pb-5 border-t ${isDark ? 'border-[#1a3884]/30' : 'border-gray-100'}`}>
              <div className="pt-4 flex flex-wrap gap-3">
                {/* Date Range */}
                <Select
                  label="Date Range"
                  value={filters.dateRange || 'year'}
                  options={DATE_OPTIONS}
                  onChange={v => update('dateRange', v)}
                  isDark={isDark}
                />

                {/* Custom date range */}
                {filters.dateRange === 'custom' && (
                  <>
                    <div className="flex flex-col gap-1 min-w-[140px]">
                      <label className={`text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>From</label>
                      <input
                        type="date"
                        value={filters.dateFrom || ''}
                        onChange={e => update('dateFrom', e.target.value)}
                        className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                      />
                    </div>
                    <div className="flex flex-col gap-1 min-w-[140px]">
                      <label className={`text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>To</label>
                      <input
                        type="date"
                        value={filters.dateTo || ''}
                        onChange={e => update('dateTo', e.target.value)}
                        className={`text-xs font-semibold rounded-xl px-3 py-2 border focus:outline-none ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                      />
                    </div>
                  </>
                )}

                {/* College */}
                {user?.role !== 'college_admin' && (
                  <Select label="College" value={filters.college || ''} options={collegeOptions} onChange={v => update('college', v)} isDark={isDark} />
                )}

                {/* Department */}
                <Select
                  label="Department"
                  value={filters.department || ''}
                  options={deptOptions}
                  onChange={v => update('department', v)}
                  isDark={isDark}
                />

                {/* Batch */}
                <Select
                  label="Batch"
                  value={filters.batch || ''}
                  options={batchOptions}
                  onChange={v => update('batch', v)}
                  isDark={isDark}
                />

                {/* User Role */}
                <Select label="User Role" value={filters.role || ''} options={ROLE_OPTIONS} onChange={v => update('role', v)} isDark={isDark} />

                {/* Status */}
                <Select label="Status" value={filters.status || ''} options={STATUS_OPTIONS} onChange={v => update('status', v)} isDark={isDark} />
              </div>

              {/* Reset */}
              {activeCount > 0 && (
                <button
                  onClick={onReset}
                  className={`mt-4 flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold border transition-all ${
                    isDark
                      ? 'border-rose-800/50 text-rose-400 hover:bg-rose-950/30'
                      : 'border-rose-200 text-rose-600 hover:bg-rose-50'
                  }`}
                >
                  <X size={12} />
                  Reset All Filters
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnalyticsFilterBar;
